/**
 * Knowledge Base CDN client (#1088, epic #1085).
 *
 * Fetches the published, locale-specific KB package (translated copy + the
 * per-section narration audio filenames) from the shared guide-content CDN,
 * written by scripts/publish-kb.py. Returns null on any failure (CDN
 * unconfigured, 404, transport / parse error) so callers fall back to the
 * bundled en-US sections (which carry no audio).
 */
import { guideCdnBaseUrl } from '../user_guides/loader';
import { ensureCdnCookies } from '../user_guides/cdnAuth';
import { KB_VERSION, KB_MODULE_ID } from './knowledgeBase.js';

/** CDN package key for the KB locale JSON + audio (matches publish-kb.py). */
export const KB_PACKAGE_KEY = `${KB_MODULE_ID}/${KB_VERSION}/`;

/**
 * Fetch the published sections for a locale.
 * @param {string} locale e.g. "en-US"
 * @returns {Promise<object[]|null>} sections (with `audio`), or null on failure.
 */
export const fetchKbSections = async (locale) => {
  let base;
  try {
    base = guideCdnBaseUrl();
  } catch {
    return null;
  }
  try {
    // Best-effort cookie freshness (#1566): a false just means this fetch may
    // 403, which lands in the same null fallback.
    await ensureCdnCookies();
    const url = `${base}/${KB_PACKAGE_KEY}${encodeURIComponent(locale)}.json`;
    let res = await fetch(url, { credentials: 'include' });
    if (res.status === 403) {
      await ensureCdnCookies({ force: true });
      res = await fetch(url, { credentials: 'include' });
    }
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data && data.sections) ? data.sections : null;
  } catch {
    return null;
  }
};
